<template>
  <section
    class="AiMapSection"
    :aria-label="$t('accessibility.description.hotelLocationInformations')">
    <ai-map
      v-if="!isSmallDevice && arePropertiesFilled"
      class="AiMap-map"
      :center="center as Position"
      :hotel-position="hotelPosition as Position"
      :first-ring="firstRing as Position"
      :second-ring="secondRing as Position" />
    <article
      class="Layout"
      :aria-label="$t('accessibility.description.hotelLocationInformation')">
      <div class="Layout-section">
        <grid-item class="AiMap-descriptionSection" sm="4" md="3" offset-md="1">
          <ai-typo class="AiMap-title" as="h2" variant="heading-02">
            {{ $t(`hotel.map.title`) }}
          </ai-typo>
          <ai-map
            v-if="isSmallDevice && arePropertiesFilled"
            class="AiMap-map"
            :center="hotelPosition as Position"
            :hotel-position="hotelPosition as Position"
            :first-ring="firstRing as Position"
            :second-ring="secondRing as Position" />
          <div class="AiMap-section">
            <address class="AiMap-Address">
              <ai-link
                class="AiMap-link"
                target="_blank"
                :text="completAddress"
                :url="googleUrl"
                typo-variant="paragraph-02-bold" />
              <span class="AiMap-phone">
                <ai-typo as="span" variant="paragraph-04-regular">
                  {{ $t('hotel.map.phone') }}
                </ai-typo>
                <ai-link
                  :url="`tel:+${phoneNumber}`"
                  typo-variant="paragraph-04-regular">
                  <ai-typo as="span" variant="paragraph-04-regular">
                    {{ phoneNumber }}
                  </ai-typo>
                </ai-link>
              </span>
              <span class="AiMap-mailContent">
                <ai-link
                  class="BookingSummaryCard-mailCta"
                  undecorated
                  :text="$t('hotel.map.mail')"
                  :url="`mailto:${contact?.mail ?? ''}`"
                  typo-variant="paragraph-04-regular">
                  <ai-typo
                    class="AiMap-mail"
                    as="span"
                    variant="paragraph-04-regular">
                    {{ contact?.mail ?? '' }}
                  </ai-typo>
                </ai-link>
              </span>
            </address>

            <ai-separator :margin="2" />

            <ai-map-section
              v-if="surroundings?.nearbyAirports?.length"
              label="airport"
              :sections="surroundings?.nearbyAirports" />

            <ai-separator
              v-if="surroundings?.nearbyAirports?.length"
              :margin="2" />

            <ai-map-section
              v-if="surroundings?.nearbyAttractions?.length"
              label="amusement-park"
              :sections="surroundings?.nearbyAttractions" />
          </div>
        </grid-item>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import AiLink from '../../atoms/AiLink/AiLink.vue';
import AiMap from '../../atoms/AiMap/AiMap.vue';
import AiSeparator from '../../atoms/AiSeparator/AiSeparator.vue';
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiMapSection from '../../molecules/AiMapSection/AiMapSection.vue';

import type {
  AddressFragment,
  ContactFragment,
  SurroundingsFragment,
} from '~~/domains/graphql';

export interface AiMapProps {
  contact: ContactFragment;
  address: AddressFragment;
  surroundings: SurroundingsFragment;
  hotelName?: string;
}

export type Position = {
  lat: number;
  lng: number;
};

const props = defineProps<AiMapProps>();

const isSmallDevice = useMatchMediaQuery('tablet', true);

const completAddress = computed(() => {
  if (!props.address) return;

  const { street, zipCode, city, country } = props.address;

  return `${street}, ${zipCode}, ${city}, ${country}`;
});

const googleUrl = computed(() => {
  if (!props.address) return;

  const { city, country, street, zipCode } = props.address;

  const rawAdress = [props.hotelName, street, city, zipCode, country]
    .filter(Boolean)
    .join(', ');
  const encodedAdress = encodeURIComponent(rawAdress);

  return `https://www.google.com/maps/search/?api=1&query=${encodedAdress}&z=14&t=m&hl=fr-FR&gl=US&mapclient=apiv3`;
});

const center = computed(() => {
  if (!props.address) return;

  return {
    lat: parseFloat(props.address?.lat ?? '') - 0.004,
    lng: parseFloat(props.address?.lng ?? '') - 0.011,
  };
});

const hotelPosition = computed(() => {
  if (!props.address) return;

  return {
    lat: parseFloat(props.address?.lat ?? ''),
    lng: parseFloat(props.address?.lng ?? ''),
  };
});

const firstRing = computed(() => {
  if (!props.address || isSmallDevice.value) return;

  return {
    lat: parseFloat(props.address?.lat ?? '') - 0.005,
    lng: parseFloat(props.address?.lng ?? ''),
  };
});

const secondRing = computed(() => {
  if (!props.address || isSmallDevice.value) return;

  return {
    lat: parseFloat(props?.address?.lat ?? '') - 0.013,
    lng: parseFloat(props?.address?.lng ?? ''),
  };
});

const arePropertiesFilled = computed<boolean>(() =>
  Boolean(center && hotelPosition && firstRing && secondRing),
);

const phoneNumber = computed(
  () => props.contact?.phonePrefix + ' ' + props.contact?.phone,
);
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';
.AiMap-section,
.AiMap-Address {
  display: flex;
  flex-direction: column;
}
.AiMap-descriptionSection {
  padding-top: constants.$padding-07;
  padding-bottom: constants.$padding-07;

  @media (mq.$from-medium) {
    z-index: 1;
  }
}
.AiMap-title {
  margin-bottom: constants.$margin-04;
}
.AiMap-link {
  margin-bottom: constants.$inner-02;
  text-decoration: underline;
  text-decoration-color: colors.$stratos-900;
}
.AiMap-mailContent {
  margin-bottom: constants.$inner-01;
}
.AiMap-mail {
  text-decoration: underline;
}
.AiMapSection {
  background-color: colors.$gold-100;

  @media (mq.$from-medium) {
    position: relative;
  }
}
.AiMap-map {
  margin: 0 auto;
  margin-bottom: constants.$margin-02;

  @include mixins.rem-fallback(width, 327);
  @include mixins.rem-fallback(height, 272);

  @media (mq.$from-medium) {
    width: 100%;
    height: 100%;
    position: absolute;
  }
}

.AiMap-phone {
  margin-bottom: constants.$inner-01;
}
</style>

<style lang="scss">
.gmnoprint {
  visibility: hidden;
}
.gm-style-cc {
  visibility: hidden;
}
.vue-map {
  div {
    a {
      visibility: hidden;
    }
  }
}
</style>
